import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import type { UserUsageResponse, AnalyticsDailyPoint, SetupStatusResponse } from '../services/apiService';
import { funnelUnits } from '../utils/funnel-units';
import DeltaBadge from '../components/DeltaBadge';
import WebsiteVerification from '../components/WebsiteVerification';
import ActivationChecklist from '../components/ActivationChecklist';
import FeedHealthBanner from '../components/FeedHealthBanner';
import CatalogCoveragePanel from '../components/CatalogCoverage';

// The Dashboard is a glance page: the SAME four funnel numbers and labels as
// Analytics (summed from the same analytics daily series, 30-day window) —
// one model per number, one vocabulary across both pages.
interface FunnelTotals {
  pageviews: number;
  delivered: number;
  viewed: number;
  clicks: number;
}

// The same four quantities over the previous equal-length window, as served.
type FunnelComparison = Partial<Record<'impressions' | 'matches' | 'views' | 'clicks', number>>;

// Period bounds are UTC dates — format in UTC so a viewer west of Greenwich
// doesn't otherwise see the previous day.
function formatUtcDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { timeZone: 'UTC' });
}

// periodEnd is an EXCLUSIVE UTC bound; show the day before it so the range
// reads as inclusive (e.g. "Jan 10 – Jan 9 next year").
function formatPeriodEnd(iso: string): string {
  const inclusiveEnd = new Date(new Date(iso).getTime() - 86400000);
  return formatUtcDate(inclusiveEnd.toISOString());
}

// The allowance window is a YEAR — computeUsagePeriod anchors it to the account's
// creation anniversary — and the industry default assumption is a MONTH. So a
// publisher reading "10,679 / 100,000" seven weeks in reads it as a tenth of a
// monthly budget, which is alarming, when it is a tenth of an annual one. Two
// dates they must subtract is not a statement of the window.
//
// DERIVED from the served bounds rather than hardcoded: if the server's period
// ever stops being a year, this copy follows instead of lying about it.
function periodLengthLabel(startIso: string, endIso: string): string {
  const days = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 86_400_000);
  if (days >= 300) return 'this plan year';
  if (days >= 25) return 'this month';
  return 'this period';
}

// "Am I about to run out?" is a question about PACE, and the panel answered only
// the LEVEL. A bar at 11% says nothing on its own: 11% used is comfortable a
// sixth of the way through a year and alarming a fiftieth of the way in, and the
// only way to tell was to subtract the two dates printed underneath.
//
// Measured on the live account 2026-09-06 (through the route's own
// computeUsagePeriod): period 2026-07-14 → 2027-07-14, 10,818 of 100,000 used,
// 15% of the period elapsed. So the honest reading is "comfortably ahead", and
// nothing on the screen said so.
//
// A FACT, NOT A FORECAST. "At this rate you will use 72,000" was considered and
// refused: the free tier is not enforced today, and a projection on a meter
// nobody is charged against is a number that changes no decision. Elapsed share
// is simply true.
function elapsedPercent(startIso: string, endIso: string, now: Date): number | null {
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (!(end > start)) return null;
  return Math.min(100, Math.max(0, Math.round(((now.getTime() - start) / (end - start)) * 100)));
}

const errorBannerStyle: React.CSSProperties = {
  gridColumn: 'span 2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '1rem',
  padding: '1rem',
  backgroundColor: 'var(--danger-bg)',
  color: 'var(--danger)',
  borderRadius: 'var(--radius)',
  border: '1px solid rgba(180,35,24,0.24)',
};

const retryButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  backgroundColor: 'var(--danger)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const usageBarTrackStyle: React.CSSProperties = {
  width: '100%',
  height: '10px',
  borderRadius: 'var(--radius)',
  backgroundColor: 'var(--line)',
  overflow: 'hidden',
};

const usageBarFillStyle = (percent: number): React.CSSProperties => ({
  width: `${Math.min(100, Math.max(0, percent))}%`,
  height: '100%',
  backgroundColor: percent >= 100 ? 'var(--danger)' : 'var(--good)',
});

const usageBadgeStyle: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: '0.5rem',
  padding: '0.15rem 0.5rem',
  fontSize: '0.75rem',
  fontWeight: 600,
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--warn-bg)',
  color: 'var(--warn)',
};

// Inline error state with a retry action; testids are `${id}-error` / `${id}-retry`
const ErrorCard: React.FC<{ id: string; message: string; onRetry: () => void }> = ({ id, message, onRetry }) => (
  <div data-testid={`${id}-error`} style={errorBannerStyle}>
    <span>{message}</span>
    <button data-testid={`${id}-retry`} onClick={onRetry} style={retryButtonStyle}>
      Retry
    </button>
  </div>
);

const DashboardPage: React.FC = () => {
  // null until the first fetch resolves — a slow request must show a loading
  // placeholder, never a misleading 30-day zero (light-review finding).
  const [funnel, setFunnel] = useState<FunnelTotals | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [daily, setDaily] = useState<AnalyticsDailyPoint[]>([]);
  // Previous equal period. null while loading and when the server omits it, so a
  // missing comparison renders no badge rather than a delta against zero — which
  // would read as "new" on a screen that has simply not loaded yet.
  const [comparison, setComparison] = useState<FunnelComparison | null>(null);
  const [viewTrackingStartedAt, setViewTrackingStartedAt] = useState<string | null>(null);
  const [usage, setUsage] = useState<UserUsageResponse | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  // ONE setup-status fetch for the two children that read it. Both used to call
  // it themselves, so a dashboard load ran the endpoint's three queries twice.
  // The page owns the request; each child still owns what it renders and how it
  // fails — the banner stays silent on a null status, the checklist offers a
  // retry, and neither can be hidden by the other's render decisions.
  const [setup, setSetup] = useState<SetupStatusResponse | null>(null);
  const [setupLoading, setSetupLoading] = useState(true);
  const [setupError, setSetupError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
    fetchUsage();
    fetchSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSetup = async () => {
    setSetupLoading(true);
    setSetupError(null);
    try {
      setSetup(await apiService.getSetupStatus());
    } catch (error) {
      console.error('Failed to fetch setup status:', error);
      setSetupError('Could not load your setup status.');
    } finally {
      setSetupLoading(false);
    }
  };

  const fetchUsage = async () => {
    setUsageError(null);
    try {
      const data = await apiService.getUsage();
      setUsage(data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
      setUsageError('Could not load usage.');
    }
  };

  const fetchStats = async () => {
    setStatsError(null);
    try {
      const data = await apiService.getAnalytics();
      // Same daily series Analytics sums for its funnel tiles (30d default).
      // The series itself is kept, not just the four sums, because the sub-lines
      // that make those sums READABLE need it — see below.
      setDaily(data.daily);
      setViewTrackingStartedAt((data as any).viewTrackingStartedAt ?? null);
      setComparison(data.comparison ?? null);
      setFunnel({
        pageviews: data.daily.reduce((s, d) => s + (d.impressions ?? 0), 0),
        delivered: data.daily.reduce((s, d) => s + d.matchesServed, 0),
        viewed: data.daily.reduce((s, d) => s + d.views, 0),
        clicks: data.daily.reduce((s, d) => s + d.clicks, 0),
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setStatsError('Could not load analytics.');
    }
  };

  // THESE FOUR NUMBERS ARE THREE DIFFERENT UNITS — pages, overlays, clicks — and
  // read as a descending funnel they do not descend. On the live account over 30
  // days: 6,648 pageviews, 37,666 overlays delivered, 22,559 viewed, 39 clicks.
  // Delivered is 5.7x pageviews because a page carries many images. The
  // arithmetic is right; without the unit the sequence reads as impossible, and
  // this page had no period control to disambiguate it either.
  //
  // Analytics carried these sub-lines all along. Shared rather than copied — the
  // two screens sum the same series to the same numbers, and a rule stated twice
  // eventually gets stated differently.
  const units = funnelUnits({ daily, viewTrackingStartedAt, periodLabel: 'last 30 days' });

  // WITHOUT THESE, THE SCREEN CANNOT REPORT A FALL. Measured on the live account
  // 2026-09-06 over the served 30-day windows: pageviews 6,496 vs 4,282 (+52%),
  // overlays delivered 36,720 vs 24,988, viewed 21,685 vs 12,879 — and product
  // clicks 39 vs 90, DOWN 57%. Three numbers up, one halved, and a page whose
  // whole job is "is everything working?" showed four bare counts. The previous
  // period was already on the wire and only Analytics read it.
  //
  // `previous` is paired with the value rendered above it, which the Dashboard
  // sums from `daily` — see DeltaBadge for why that pairing is the correct one.
  const funnelCards: Array<{ label: string; value: number | null; sub: string; testId: string; previous?: number }> = [
    { label: 'Pageviews', value: funnel?.pageviews ?? null, sub: units.pageviews, testId: 'dash-funnel-pageviews', previous: comparison?.impressions },
    { label: 'Overlays delivered', value: funnel?.delivered ?? null, sub: units.delivered, testId: 'dash-funnel-delivered', previous: comparison?.matches },
    { label: 'Overlays viewed', value: funnel?.viewed ?? null, sub: units.viewed, testId: 'dash-funnel-viewed', previous: comparison?.views },
    { label: 'Product clicks', value: funnel?.clicks ?? null, sub: units.clicks, testId: 'dash-funnel-clicks', previous: comparison?.clicks },
  ];

  return (
    <>
      {/* ABOVE the checklist, and outside it. A struck-out feed is the one thing
          on this page that is actively costing the publisher something right
          now, and the checklist renders nothing once setup is complete — which
          is exactly the publisher who has been serving a frozen catalogue for
          days without a word from us. */}
      <FeedHealthBanner status={setup} />

      {/* First-run activation checklist — primary surface until setup completes */}
      <ActivationChecklist status={setup} loading={setupLoading} error={setupError} onRetry={fetchSetup} />

      {/* Performance Overview — glance funnel, same labels/values as Analytics */}
      <section className="stats-section" data-testid="api-key-section">
        <h2>Performance Overview</h2>
        <div className="stats-grid">
          {statsError ? (
            <ErrorCard id="stats" message={statsError} onRetry={fetchStats} />
          ) : (
            funnelCards.map(card => (
              <div className="stat-card" key={card.testId} data-testid={card.testId}>
                <h3>{card.label}</h3>
                {/* justifyContent IS LOAD-BEARING. `.stat-card` centres its
                    contents with `text-align: center`, and a flex container
                    ignores that for its own items — they pack to flex-start. So
                    wrapping the value to sit the delta beside it silently
                    left-aligned the number while the label above and the unit
                    line below stayed centred. Shipped that way on 2026-09-06 and
                    caught on a real phone, not by the measurement written to
                    check this: that one compared the badge's position to the
                    VALUE's and never to the CARD's. */}
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <div className="stat-value">{card.value != null ? card.value.toLocaleString() : '…'}</div>
                  {card.value != null && card.previous != null && (
                    <span data-testid={`${card.testId}-delta`}>
                      <DeltaBadge current={card.value} previous={card.previous} />
                    </span>
                  )}
                </div>
                {card.value != null && (
                  <div className="no-data" style={{ fontSize: '0.72rem', marginTop: '0.15rem' }}>{card.sub}</div>
                )}

              </div>
            ))
          )}
        </div>
        <p className="no-data" style={{ marginBottom: 0 }}>
          Last 30 days, compared with the 30 days before — full detail on the Analytics page.
        </p>
      </section>

      {/* Usage Meter */}
      {/* Catalogue coverage sits after the headline numbers: it explains the
          gap between "images we saw" and "overlays shown", which is otherwise
          invisible to the publisher (feed-coverage-visibility). */}
      <CatalogCoveragePanel />

      <section className="stats-section" data-testid="usage-section">
        <h2>
          Plan &amp; usage
          <span style={usageBadgeStyle}>Free beta — limits not enforced</span>
        </h2>
        {usageError ? (
          <ErrorCard id="usage" message={usageError} onRetry={fetchUsage} />
        ) : usage ? (
          <div>
            <p data-testid="usage-summary">
              <strong>{usage.used.toLocaleString()}</strong> / {usage.allowance.toLocaleString()} widget pageviews
              {' '}{periodLengthLabel(usage.periodStart, usage.periodEnd)}
              {' '}on the <strong>{usage.plan}</strong> plan
            </p>
            <p className="no-data" style={{ marginTop: 0 }}>
              One widget pageview = one page load where your widget runs.
            </p>
            <div style={usageBarTrackStyle}>
              <div
                data-testid="usage-bar-fill"
                style={usageBarFillStyle(usage.allowance > 0 ? (usage.used / usage.allowance) * 100 : 0)}
              />
            </div>
            {/* Only when the meter covered the whole period. Where it did not,
                the used share UNDERSTATES, so pairing it with the elapsed share
                would read as "comfortably ahead" on a number known to be short —
                the note below says so instead, and says it alone. One production
                account is in that state today. */}
            {usage.completeForPeriod && usage.allowance > 0 && elapsedPercent(usage.periodStart, usage.periodEnd, new Date()) != null && (
              <p className="no-data" data-testid="usage-pace">
                Used {Math.round((usage.used / usage.allowance) * 100)}% of the allowance
                {' '}with {elapsedPercent(usage.periodStart, usage.periodEnd, new Date())}% of
                {' '}{periodLengthLabel(usage.periodStart, usage.periodEnd)} gone.
              </p>
            )}
            <p className="no-data">
              Period: {formatUtcDate(usage.periodStart)} – {formatPeriodEnd(usage.periodEnd)}
            </p>
            {!usage.completeForPeriod && (
              <p className="no-data" data-testid="usage-incomplete-note">
                Pageview tracking started partway through this period — the total above may be incomplete.
              </p>
            )}
          </div>
        ) : null}
      </section>

      {/* Website Verification — anchor target for the checklist's verify step
          and the header's verification-status link. The component renders
          nothing once verified (the header badge owns that state). */}
      <div id="website-verification">
        <WebsiteVerification />
      </div>
    </>
  );
};

export default DashboardPage;
